import { blockerOf } from '../blocked';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useApp } from '../store';
import { confirmDialog } from '../confirm';
import { DOCUMENT_KIND, DOCUMENT_STATE } from '../documents';
import { can, canSeeCompletionOdds } from '../permissions';
import FinishSprint from './FinishSprint';
import DateField from './DateField';
import { useInvalidDates } from './useInvalidDates';
import MoscowNudge from './MoscowNudge';
import {
  DEFAULT_SPRINT_DAYS,
  closingAsks,
  SPRINT_STATE_LABEL,
  addDays,
  canEditSprint,
  committedAndAdded,
  editableParts,
  inOrder,
  inSprint,
  nextAfter,
  notInAnySprint,
  offerFromBacklog,
  plannable,
  sprintLength,
  suggestedDates,
  suggestedName,
  timeLeft,
  type Leftovers,
  type Sprint,
} from '../sprints';
import { readyFirst } from '../next-up';
import { sprintOdds } from '../forecast';
import { PRIORITY_LABELS, SIZE_LABEL, STATUS_LABELS } from '../types';
import { capacityOf, totalPoints, typicalVelocity, velocities } from '../burndown';
import Burndown from './Burndown';
import CutRelease from './CutRelease';
import Icon from './Icon';
import SprintSignoff from './SprintSignoff';
import { readPolicy, usesSlots } from '../signoff-policy';
import { asDayMonth } from '../utils';

/**
 * THE FORTNIGHT, AND WHAT IS IN IT.
 *
 * ONE SCREEN, NOT THREE. Planning a sprint, watching it and closing it are the
 * same three questions asked at different points — what is in it, how much is
 * left, and what happens now — and splitting them across screens is how a team
 * plans a sprint and then never opens it again. The running one is at the top
 * because that is what somebody arrives to look at; planning is below it, where
 * it is out of the way until Thursday of the second week.
 *
 * ONE OPEN AT A TIME, and the running one is open when you arrive. A board that
 * has run for a year has thirty sprints on this screen; expanded, every one of
 * them shows its whole contents and the fortnight somebody actually came to look
 * at is four screens down. Same shape as the Teams screen, for the same reason.
 *
 * PLANNING HAPPENS INSIDE THE SPRINT BEING PLANNED. An earlier version put one
 * "pull work in" list at the foot of the screen, wired to whichever sprint was
 * running — which meant the one thing planning IS (filling the NEXT fortnight,
 * before it starts) could not be done at all. Work is added to the sprint whose
 * panel it is in, and that is true of the running one and the planned ones
 * alike.
 */
export default function Sprints() {
  const sprints = useApp((s) => s.sprints);
  const issues = useApp((s) => s.issues);
  const workCalendar = useApp((s) => s.workCalendar);
  const projects = useApp((s) => s.projects);
  const projectId = useApp((s) => s.projectId);
  const role = useApp((s) => s.shownRole);
  const createSprint = useApp((s) => s.createSprint);
  const startSprint = useApp((s) => s.startSprint);
  const editSprint = useApp((s) => s.editSprint);
  const closeSprint = useApp((s) => s.closeSprint);
  const deleteSprint = useApp((s) => s.deleteSprint);
  const putInSprint = useApp((s) => s.putInSprint);
  const moveIssue = useApp((s) => s.moveIssue);
  const moveIssues = useApp((s) => s.moveIssues);
  const openIssue = useApp((s) => s.openIssue);
  const updateIssue = useApp((s) => s.updateIssue);
  const moscowNudge = useApp((s) => s.moscowNudge);
  const dismissMoscowNudge = useApp((s) => s.dismissMoscowNudge);
  /*
   * DELIBERATELY THE SAME TWO RANKS AS THE DATABASE, which is what actually
   * decides — see the policies at the foot of migration 0019. Planning a sprint
   * and moving a card between columns are the same act (deciding where work
   * sits), and deleting a sprint sits with deleting a card. Offering a button
   * the database will refuse is worse than not offering it: the refusal arrives
   * as nothing happening.
   *
   * A viewer sees the sprint and everything in it and can change none of it,
   * which is the whole point of showing them.
   */
  const mayPlan = can(role, 'moveIssue');
  /*
   * PICKED, BUT NOT YET ADDED. Adding a fortnight's work one card at a time is
   * the shape of the screen arguing with the job — asked for on 19 August:
   * "able to select multiple rows to be added to the sprint for bulk add".
   *
   * Kept by card id rather than by row, so a search that changes what is on
   * screen does not silently change what is about to be added.
   */
  const [picked, setPicked] = useState<Set<string>>(new Set());
  /* A refusal has to land somewhere the eye already is — beside the button. */
  const [addProblem, setAddProblem] = useState('');
  const [adding, setAdding] = useState(false);
  /* The one-shot at the top of a running sprint's contents — see `parked`. */
  const [starting, setStarting] = useState(false);
  const pick = (id: string, on: boolean) =>
    setPicked((was) => {
      const next = new Set(was);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  const mayDelete = can(role, 'deleteIssue');
  // The rank `builds_write` asks for, so the offer is never made to somebody
  // the database would refuse.
  const mayRelease = can(role, 'editSettings');
  const assistantVersions = useApp((s) => s.meta.assistantVersions ?? false);
  /* Whether this board has opened the completion odds to everyone (0220); absent reads as
     off — a PM-and-above read. The one gate shared with the card modal so visibility never
     drifts between surfaces (src/permissions.ts canSeeCompletionOdds). */
  const maySeeOdds = canSeeCompletionOdds(role, useApp((s) => s.meta.showOddsToEveryone === true));
  /* On a slots board the sprint carries its own two-phase sign-off (authorise,
     then deliver), and authorisation is offered from planning — so the panel
     shows for every state, not only when the sprint is closed. A rank board
     keeps the finished-only inline sheet. */
  const signoffPolicy = useApp((s) => s.meta.signoffPolicy);
  const slotsSignoff = usesSlots(readPolicy(signoffPolicy));

  const ordered = inOrder(sprints);
  /*
   * WHAT COULD GO INTO A SPRINT: in no sprint, and not already resolved.
   *
   * `notInAnySprint` alone is every card without a sprint, FINISHED WORK
   * INCLUDED — on PhotoSyncApp that is 192 cards of which 138 are done, so the
   * heading offered to plan a fortnight out of work that had already shipped.
   * Planning is about what is left, so what is left is what this holds.
   */
  const backlog = plannable(issues);
  /*
   * WHAT THIS TEAM TYPICALLY GETS THROUGH, worked out once for the screen
   * rather than per sprint card. It is the same answer for all of them — it is
   * a fact about the team, not about the fortnight being looked at — and it
   * replays every closed sprint's history to arrive at, which is not something
   * to do five times on one render.
   */
  const history = useMemo(() => velocities(issues, sprints), [issues, sprints]);
  const typical = typicalVelocity(history);

  const [problem, setProblem] = useState('');
  const [planning, setPlanning] = useState(false);
  /*
   * WHICH SPRINT IS BEING FINISHED, while the question is on screen. Local to
   * this screen rather than in the store, because the only way to reach it is
   * the button six lines below — unlike WhyClosed, which a card move triggers
   * from anywhere and so has to live where anywhere can reach it.
   */
  const [finishing, setFinishing] = useState('');
  /*
   * CHANGING ONE AFTER IT EXISTS. `editing` is the sprint's id, and `edit` the
   * values being typed — held apart from `draft`, which belongs to the planner
   * at the bottom of the screen. One object for both would mean opening the
   * editor overwrote a half-planned sprint somebody had not finished typing.
   */
  const [editing, setEditing] = useState('');
  const [edit, setEdit] = useState({ name: '', goal: '', startsOn: '', endsOn: '' });
  // Hold Save/Plan it while a date box holds an entry it could not read. The
  // inline editor and the planner can be open at once, so each has its own
  // tracker (see useInvalidDates) — one's flag must not block the other.
  const badEdit = useInvalidDates();
  const badPlan = useInvalidDates();
  /*
   * OPTION A, AND IT LIVES HERE RATHER THAN IN THE CLOSING DIALOG.
   *
   * The mockup put a "record a release" tick inside that dialog, and building
   * it showed why that is wrong: the dialog only appears when there is
   * UNFINISHED WORK to place. A sprint that finished everything closes on the
   * press with no dialog at all — and that is the sprint most likely to have
   * shipped something. The tick would have been missing from exactly the case
   * it was for.
   *
   * So the offer comes AFTER the close instead, on both paths, and it is an
   * offer rather than a step: a team that releases monthly closes four sprints
   * and dismisses this four times, which must cost one press and no dialog.
   *
   * AFTER, NOT DURING, for a second reason. Closing can fail. A release
   * recorded in the same breath would be claiming a fortnight that never
   * actually ended.
   */
  const [shipped, setShipped] = useState('');
  const [releasing, setReleasing] = useState('');
  const [busy, setBusy] = useState(false);
  /*
   * WHICH SPRINT IS EXPANDED. `null` means "nobody has chosen yet", which is
   * different from "none open" — it is what lets the running one be open on
   * arrival while still allowing it to be closed. A plain empty string could
   * not tell the two apart, so collapsing the running sprint would have sprung
   * straight back open on the next render.
   */
  const [open, setOpen] = useState<string | null>(null);
  /*
   * ── AND IT HAS TO BE ON SCREEN, NOT MERELY EXPANDED — #350 ────────────────
   *
   * A board that has run for a year has thirty sprints here. Opening the right
   * one two thousand pixels down is not "you can immediately go to approve it",
   * which is what was actually asked for; it is the same hunt with one fewer
   * click. Runs after paint, so the sprint has been expanded before it is
   * scrolled to and the browser measures the open height rather than the shut
   * one.
   */
  const revealId = useApp((s) => s.revealId);
  useEffect(() => {
    if (!revealId) return;
    const el = document.getElementById(`sprint-${revealId}`);
    el?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [revealId]);
  /** Narrows the backlog while planning. One box, whichever sprint is open. */
  const [find, setFind] = useState('');
  const [draft, setDraft] = useState(() => ({
    name: '',
    goal: '',
    ...suggestedDates(undefined),
  }));

  /** Opens the form already filled in, so planning is a press and a check. */
  const openPlanner = () => {
    // Indexed rather than `.at(-1)`: this project's TypeScript target predates
    // it, and the build fails rather than the browser.
    const byEnd = [...sprints].sort((a, b) => a.endsOn.localeCompare(b.endsOn));
    const last = byEnd[byEnd.length - 1];
    setDraft({ name: suggestedName(sprints), goal: '', ...suggestedDates(last) });
    setProblem('');
    setPlanning(true);
  };

  const plan = async () => {
    if (badPlan.ref.current) return;   // a date box holds something unreadable
    const wrong = await createSprint(draft);
    if (wrong) return setProblem(wrong);
    setProblem('');
    setPlanning(false);
  };

  const act = async (p: Promise<string>) => setProblem(await p);

  /** Open the editor on a sprint, filled with what it currently says. */
  const startEditing = (s: Sprint) => {
    setProblem('');
    setEditing(s.id);
    setEdit({ name: s.name, goal: s.goal ?? '', startsOn: s.startsOn, endsOn: s.endsOn });
  };

  /*
   * SAVING SENDS ONLY WHAT THIS STATE ALLOWS. Filtering here as well as
   * disabling the boxes is not belt and braces: `editableParts` is the rule,
   * and a screen that merely greys a field out is one refactor away from
   * sending it anyway.
   */
  const saveEdit = async (s: Sprint) => {
    if (badEdit.ref.current) return;   // a date box holds something unreadable
    const allowed = editableParts(s.state);
    const patch: Partial<Sprint> = {};
    if (allowed.name) patch.name = edit.name.trim();
    if (allowed.goal) patch.goal = edit.goal.trim();
    if (allowed.startsOn) patch.startsOn = edit.startsOn;
    if (allowed.endsOn) patch.endsOn = edit.endsOn;
    const said = await editSprint(s.id, patch);
    setProblem(said);
    if (!said) setEditing('');
  };

  /**
   * FINISHING ONE. Nothing unfinished means nothing to ask, so it closes on the
   * press — see `closingAsks`. Otherwise the question goes up, and the answer
   * comes back through `finish` below.
   */
  const askThenFinish = (s: Sprint) => {
    setProblem('');
    const { ask } = closingAsks(s, sprints, issues);
    if (!ask) {
      // A sprint that finished everything closes on the press — and is exactly
      // the one worth offering a release for. See `shipped` above.
      return void closeSprint(s.id, 'backlog').then((wrong) => {
        setProblem(wrong);
        if (!wrong) setShipped(s.id);
      });
    }
    setFinishing(s.id);
  };

  const finish = async (what: Leftovers, alsoStart: boolean) => {
    setBusy(true);
    const closed = finishing;
    const finishedName = sprints.find((s) => s.id === finishing)?.name ?? 'The sprint';
    /*
     * THE NEXT SPRINT IS READ BEFORE THE CLOSE, not after. Closing carries work
     * into it, and `nextAfter` answers "the earliest PLANNED one" — so asking
     * afterwards would still be right today and silently wrong the moment
     * closing ever changes another sprint's state. Read it while the question
     * it answers is still the one that was asked.
     */
    const next = alsoStart ? nextAfter(sprints) : undefined;
    const wrong = await closeSprint(finishing, what);
    if (!wrong && next) {
      /*
       * A FAILURE HERE IS NOT A FAILURE TO CLOSE. The sprint did close; what
       * did not happen is the start, and saying "could not finish the sprint"
       * would send somebody looking for a problem that is not there.
       */
      const notStarted = await startSprint(next.id);
      setBusy(false);
      setProblem(notStarted ? `${finishedName} is finished. ${next.name} did not start — ${notStarted}` : '');
      setFinishing('');
      setShipped(closed);
      return;
    }
    setBusy(false);
    setProblem(wrong);
    // Held open on a refusal, so the reason is read beside the choice that
    // caused it rather than behind a dialog that has already gone.
    if (!wrong) {
      setFinishing('');
      setShipped(closed);
    }
  };

  /**
   * HOW FAR THROUGH, IN CARDS *AND* IN POINTS.
   *
   * It was cards alone, on the argument that "4 of 11 finished" needs nothing
   * explaining while points belong where they are reasoned about — the burndown,
   * and the capacity line while planning.
   *
   * HALF OF THAT WAS WRONG, and the half that was wrong only shows on a running
   * sprint. The capacity line is drawn for a sprint that has NOT started, so the
   * moment one starts, the only figure left is the card count and the chart. The
   * number a team plans in disappears at exactly the point they start asking
   * whether they will finish. Vinnie, 20 August 2026, of a sprint sitting at 12
   * of 52 points: "the sprint in FDD isn't updating with the points as being
   * completed." It was not failing to update; it was never shown.
   *
   * Cards stay first, because that argument still holds for a glance. Points
   * come after them, and only when the cards have been sized at all — a board
   * that does not size its work should not be told "0 of 0 points".
   *
   * UNSIZED CARDS ARE NAMED, NOT ABSORBED. `totalPoints` counts them separately
   * for the reason burndown.ts sets out: zero is a claim that a card is no work,
   * and a total that quietly swallows five unsized cards is a total that reads
   * as complete when it is not.
   */
  const progress = (s: Sprint) => {
    const mine = inSprint(issues, s.id);
    const finished = mine.filter((i) => i.status === 'done' || i.status === 'wontdo');
    const all = totalPoints(mine);
    const got = totalPoints(finished);
    return {
      done: finished.length,
      total: mine.length,
      points: got.points,
      ofPoints: all.points,
      unsized: all.unsized,
    };
  };

  /**
   * WHAT THE BACKLOG OFFERS WHILE PLANNING, best first and narrowed by the box.
   *
   * CAPPED, AND THE CAP IS WHY THE BOX IS THERE. A backlog is hundreds of cards
   * and rendering all of them under every sprint is both slow and useless — but
   * a list that simply stops reads as "that is everything", which is how work
   * goes missing from a plan. So the cap is stated, and the sentence stating it
   * points at the thing that fixes it.
   */
  const offerable = (limit = 25) => offerFromBacklog(backlog, find, readyFirst, limit);

  const sprintCard = (s: Sprint) => {
    const { done, total, points, ofPoints, unsized } = progress(s);
    const mine = inSprint(issues, s.id);
    const { added } = committedAndAdded(issues, s);
    const isRunning = s.state === 'active';
    /* WILL THIS SPRINT FINISH? (Layer B · piece B) — the chance its still-open cards are all
       done by its end date, from the board's own pace (forecast.ts sprintOdds, the same
       Monte-Carlo resample the go-live and per-card odds use). Only for the RUNNING sprint:
       a planned one has not started burning down and a finished one is already decided. It
       withholds (null → no chip) when there is no finished history to resample or nothing
       open is left. Backlog counts as remaining work on a project, not a continuous board. */
    const project = projects.find((p) => p.id === projectId);
    const odds = isRunning && maySeeOdds
      ? sprintOdds(s, issues, Date.now(), projectId ?? '', workCalendar, project?.deliveryMode !== 'continuous')
      : null;
    /*
     * OPEN ON ARRIVAL, and "the running one" is not enough on its own.
     *
     * Between fortnights — and on a board that has planned its first sprint and
     * not started it — nothing is running, so keying on that alone left EVERY
     * sprint collapsed and the screen looking empty at exactly the moment
     * somebody had come to fill one in. `inOrder` already puts the most
     * interesting sprint first (running, then coming up, then finished), so the
     * honest default is simply: the first one.
     *
     * See `open` above for why this cannot be a plain string.
     */
    /* ARRIVED FROM APPROVALS, POINTED AT ONE OF THEM — #350. Only while nobody
       has chosen yet, so it opens the sprint somebody was sent to and still
       lets them close it; `open` above is what keeps those two apart. */
    const isOpen =
      open === null ? (revealId ? revealId === s.id : ordered[0]?.id === s.id) : open === s.id;
    const mayFill = mayPlan && s.state !== 'closed';

    const row = (
      i: (typeof mine)[number],
      state: string,
      note?: string,
      dim = false,
      /* Offered only on a card that is in the sprint and not on the
         board — see `parked`. The same row, one more thing to do
         with it, rather than a second kind of row to learn. */
      canStart = false
    ) => {
      /* #6 — nudge a just-placed card that still has no release level. */
      const showNudge = moscowNudge.includes(i.id) && !i.moscow;
      const rowEl = (
      /*
       * THE WHOLE ROW OPENS THE CARD. Vinnie, 15 August: "it looks
       * like I can click on the entire row, but I can only click on
       * the item number. My mouse pointer shows the finger." The
       * pointer was not lying about the intent, only about the
       * target — `.grp-row` has carried `cursor: pointer` since it
       * was written, while only the reference was ever wired up.
       *
       * A DIV WITH A ROLE RATHER THAN A BUTTON, because the row
       * contains its own button and a button inside a button is not
       * valid HTML. `Take out` stops the click travelling, so the
       * one place on the row that does something else still does it.
       */
      <div
        className={`grp-row is-work is-openable ${dim ? 'is-done' : ''}`}
        role="button"
        tabIndex={0}
        onClick={() => openIssue(i.id)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openIssue(i.id);
          }
        }}
      >
        <span className="link sprint-ref">{i.ref}</span>
        <div className="work-main">
          <span className="grp-name">{i.title}</span>
          {note && <span className="work-note">{note}</span>}
        </div>
        <div className="work-meta">
          <span className="work-state">{state}</span>
          {mayPlan && canStart && (
            <button
              className="btn btn-small"
              title="Put it in the To Do column"
              onClick={(e) => {
                e.stopPropagation();
                setAddProblem('');
                void moveIssue(i.id, 'todo').catch((err: Error) =>
                  setAddProblem(err?.message || 'That could not be moved.')
                );
              }}
            >
              Move to To Do
            </button>
          )}
          {mayPlan && !dim && (
            <button
              className="btn btn-small"
              onClick={(e) => {
                e.stopPropagation();
                setAddProblem('');
                /* A refusal has to reach somebody here too. Taking a
                   card out was the one direction still throwing into
                   an empty room, so a write the rules turned down
                   left the row where it was and said nothing —
                   indistinguishable from a button that does nothing. */
                void putInSprint(i.id, '').catch((err: Error) =>
                  setAddProblem(err?.message || 'That could not be taken out.')
                );
              }}
              title="Back to the backlog"
            >
              Take out
            </button>
          )}
        </div>
      </div>
      );
      if (!showNudge) return <Fragment key={i.id}>{rowEl}</Fragment>;
      return (
        <div key={i.id} className="grp-rownudge">
          {rowEl}
          <MoscowNudge
            onPick={(level) => updateIssue(i.id, { moscow: level })}
            onDismiss={() => dismissMoscowNudge(i.id)}
          />
        </div>
      );
    };
    return (
      <div key={s.id} id={`sprint-${s.id}`} className={`person-card ${isOpen ? 'is-open' : ''}`}>
        <div className="person-top">
          <span className="avatar-me person-av">
            <Icon name="board" size={14} />
          </span>
          <div className="person-id">
            <div className="person-name">
              {s.name}
              {isRunning && <span className="person-you">{timeLeft(s)}</span>}
              {/* Will it finish? — the chance the sprint's open cards are done by its end
                  date, on the app's one RAG cut. Only the running sprint, only once there is
                  pace to read it from (Layer B · piece B). */}
              {odds && (
                <span
                  className={`rag ${odds.rag === 'good' ? 'g' : odds.rag === 'warn' ? 'a' : 'r'} sprint-odds`}
                  title={
                    `${Math.round(odds.prob * 100)}% chance the ${odds.remaining} card${odds.remaining === 1 ? '' : 's'} still open finish by ${asDayMonth(s.endsOn)}` +
                    (odds.stuck > 0
                      ? ` — but ${odds.stuck} ${odds.stuck === 1 ? 'is' : 'are'} blocked, on hold, or stalled, and the board's pace cannot finish those`
                      : '') +
                    `. Read from the board's own pace; assumes normal availability.`
                  }
                >
                  <span className="dot" />
                  {Math.round(odds.prob * 100)}% to finish
                  {/* Finishing EVERYTHING is the bar, so a stuck card is the honest caveat on the
                      number — the pace won't clear it. Named here, not hidden behind the chance. */}
                  {odds.stuck > 0 && ` · ${odds.stuck} stuck`}
                </span>
              )}
            </div>
            <div className="person-sub">
              {SPRINT_STATE_LABEL[s.state]} · {asDayMonth(s.startsOn)} to {asDayMonth(s.endsOn)} ({sprintLength(s)} days) ·{' '}
              {total === 0 ? 'nothing in it yet' : `${done} of ${total} finished`}
              {/* Only where there is something to say. See `progress` above. */}
              {ofPoints > 0 && ` · ${points} of ${ofPoints} story points`}
              {unsized > 0 &&
                ` · ${unsized} unsized`}
            </div>
          </div>
          {mayPlan && canEditSprint(s.state) && (
            <button
              className="btn btn-small"
              onClick={() => (editing === s.id ? setEditing('') : startEditing(s))}
            >
              {editing === s.id ? 'Cancel' : 'Edit'}
            </button>
          )}
          {mayPlan && s.state === 'planned' && (
            <button className="btn btn-small btn-primary" onClick={() => void act(startSprint(s.id))}>
              Start it
            </button>
          )}
          {mayPlan && isRunning && (
            <button className="btn btn-small" onClick={() => askThenFinish(s)}>
              Finish it
            </button>
          )}
          {mayDelete && s.state !== 'active' && (
            <button
              className="btn btn-small btn-danger"
              onClick={async () => {
                /*
                 * THE NUMBER IS IN THE QUESTION. "Are you sure" answers nothing;
                 * "seven cards go back to the backlog" is the fact somebody
                 * needs, and it is also the reassurance — the work is not going
                 * anywhere, only the fortnight is.
                 */
                const ok = await confirmDialog({
                  title: `Delete ${s.name}?`,
                  body:
                    mine.length === 0
                      ? 'Nothing is in it.'
                      : `Its ${mine.length} ${mine.length === 1 ? 'card goes' : 'cards go'} back to the backlog — no work is deleted.`,
                  confirmLabel: 'Delete',
                  tone: 'danger',
                });
                if (ok) void deleteSprint(s.id);
              }}
            >
              Delete
            </button>
          )}
          <button
            className="btn btn-small"
            onClick={() => setOpen(isOpen ? '' : s.id)}
            aria-expanded={isOpen}
          >
            {isOpen ? 'Close' : 'Open'}
          </button>
        </div>

        {/*
          * CHANGING IT AFTER THE FACT. The same fields as the planner at the
          * bottom of this screen, in the same order and the same words — a
          * second vocabulary for the same four things is how people learn that
          * planning and editing are different operations, which they are not.
          *
          * What is offered depends on the state and not on the role, and each
          * field that is withheld SAYS WHY where it sits. A greyed box with no
          * reason reads as something broken; the reason is short and it is the
          * whole of the design here.
          */}
        {mayPlan && editing === s.id && (
          <div className="person-manage sprint-plan">
            <label className="stack">
              <span className="stack-lbl">Name <span className="req">*</span></span>
              {editableParts(s.state).name ? (
                <input
                  className="input"
                  value={edit.name}
                  onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                />
              ) : (
                <>
                  <input className="input" value={edit.name} disabled />
                  <span className="hint">
                    Fixed while it runs — a tracker push finds a sprint by its name.
                  </span>
                </>
              )}
            </label>
            <label className="stack">
              What is it for?{' '}
              <span className="hint">A sentence, not a list of tickets.</span>
              <input
                className="input"
                value={edit.goal}
                placeholder="e.g. Get sign-in working end to end"
                onChange={(e) => setEdit({ ...edit, goal: e.target.value })}
              />
            </label>
            <div className="two-up">
              {/* A LABEL AS WIDE AS ITS WORDS — see the note in Settings.tsx
                  and #353. Wrapping the field, it made the whole column a
                  target for a calendar. */}
              <div className="stack">
                <label className="stack-lbl" htmlFor={`starts-${s.id}`}>Starts <span className="req">*</span></label>
                {editableParts(s.state).startsOn ? (
                  /*
                   * IT DOES NOT DRAG THE END WITH IT, unlike the planner. There,
                   * moving the start of a SUGGESTED fortnight should keep its
                   * length. Here somebody is correcting one date they typed
                   * wrongly, and silently moving the other one is how a
                   * correction becomes a second mistake.
                   */
                  <DateField
                    id={`starts-${s.id}`}
                    ariaLabel="Starts"
                    variant="form"
                    value={edit.startsOn}
                    onChange={(v) => setEdit({ ...edit, startsOn: v })}
                    onInvalidChange={badEdit.mark('starts')}
                  />
                ) : (
                  <>
                    <DateField id={`starts-${s.id}`} ariaLabel="Starts" variant="form" value={edit.startsOn} onChange={() => {}} disabled />
                    <span className="hint">
                      It already began. Moving it would rewrite days the chart has counted.
                    </span>
                  </>
                )}
              </div>
              <div className="stack">
                <label className="stack-lbl" htmlFor={`ends-${s.id}`}>Ends <span className="req">*</span></label>
                <DateField
                  id={`ends-${s.id}`}
                  ariaLabel="Ends"
                  variant="form"
                  value={edit.endsOn}
                  onChange={(v) => setEdit({ ...edit, endsOn: v })}
                  onInvalidChange={badEdit.mark('ends')}
                />
              </div>
            </div>
            {/*
              * THE REASON GOES WHERE THE PRESS HAPPENED. There is a banner for
              * this at the top of the screen, and on a board with a few sprints
              * it is several hundred pixels above the button — so refusing a
              * date looked exactly like the Save button doing nothing.
              */}
            {problem && <p className="setup-trouble">{problem}</p>}
            <div className="grp-foot">
              <button className="btn btn-primary" onClick={() => void saveEdit(s)} disabled={badEdit.any}>
                Save
              </button>
              <button className="btn" onClick={() => { setEditing(''); setProblem(''); }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/*
          * ONE INSET FOR EVERYTHING UNDER THE HEADING.
          *
          * Vinnie, 15 August: "fix the text being too close to the edge of the
          * greyish background, it looks like there is clipping."
          *
          * It was not clipping — it was worse, because it was invisible in the
          * code. `person-top` and `person-manage` each carry their own 12px, and
          * the goal, the scope note and the chart sat BETWEEN them as direct
          * children of the card with no padding at all: measured, all three
          * began at exactly the card's own left edge. Text flush against a
          * rounded panel reads as text that has been cut off, which is why he
          * saw clipping in something that was merely unindented.
          *
          * One wrapper rather than padding on each: three elements each
          * remembering the same 12px is three chances to forget it, and the next
          * thing added between the heading and the list would have been the
          * fourth.
          */}
        <div className="sprint-brief">
          {s.goal && <p className="hint sprint-goal">{s.goal}</p>}

          {/*
            * WHAT HAS BEEN SIGNED OFF FOR THIS FORTNIGHT — #193.
            *
            * A line rather than a panel, and only when there is one: approval
            * is given for a grouped set of work, and the sprint is that group.
            * Nothing is written twice — this reads what the document says, and
            * the document itself is opened on the Documents screen.
            */}
          <SprintDocuments sprintId={s.id} />

          {/*
            * SCOPE ADDED AFTER IT STARTED, SAID ON THE SPRINT rather than saved
            * for a report nobody reads until the retrospective. This is the
            * number burndown structurally hides: work added as fast as it is
            * finished draws a healthy line straight down to zero.
            */}
          {isRunning && added.length > 0 && (
            <p className="grid-warning" role="status">
              {added.length} {added.length === 1 ? 'card was' : 'cards were'} added after this
              sprint started.
            </p>
          )}

          {/*
            * THE CHART ON A SPRINT THAT HAS RUN, THE CAPACITY LINE ON ONE BEING
            * PLANNED — never both, and never the wrong one.
            *
            * They answer different questions at different moments. Planning asks
            * "will this fit"; a running fortnight asks "are we going to finish".
            * A burndown on an unstarted sprint is a flat line at whatever was
            * put in, which teaches people the chart is decoration.
            */}
          {s.state === 'planned' && mine.length > 0 && (
            <p className="hint burn-fit">{capacityOf(mine, typical).say}</p>
          )}
          {s.state !== 'planned' && <Burndown sprint={s} issues={issues} />}
        </div>

        {isOpen && (
          <div className="person-manage">

            <div className="grp-heading">What is in it</div>
            {/*
              * ── IN THE SPRINT, AND NOT ON THE BOARD ───────────────────────
              *
              * Vinnie, 19 August 2026: "there are 7 things in Sprint 1 in
              * PhotoSyncApp, yet I am only seeing 5 items on the board."
              *
              * Five of the seven were at Backlog status. The board has four
              * columns — To Do, In Progress, In Review, Done — and no Backlog
              * column, so a card put into a sprint and never moved on is in the
              * sprint and invisible on the board.
              *
              * That is not a fault: adding to a sprint is PLANNING, and
              * planning does not start the work. The fault was that nothing
              * said so. So it says so, and offers the move rather than making
              * it — a card's status is never rewritten behind somebody's back.
              *
              * ONLY WHILE THE SPRINT IS RUNNING. A planned fortnight full of
              * unstarted cards is exactly what planning looks like, and saying
              * "five of these are not on the board" about it would be telling
              * somebody off for doing the thing correctly. Vinnie chose this
              * on 19 August, along with the shape: the grouped list, with the
              * one-shot line above it.
              *
              * `wontdo` is NOT parked. It has no column either, but it is work
              * deliberately dropped — moving it to To Do would undo a decision.
              * It stays with the finished ones, where it already was.
              */}
            {(() => {
              const parked = isRunning ? mine.filter((i) => i.status === 'backlog') : [];
              if (!parked.length) return null;
              return (
                <>
                  <div className="sprint-parked">
                    <span>
                      <b>
                        {parked.length} of these {mine.length}
                      </b>{' '}
                      {parked.length === 1 ? 'is' : 'are'} not on the board yet —{' '}
                      {parked.length === 1 ? 'it is' : 'they are'} still in the backlog.
                    </span>
                    {mayPlan && (
                      <button
                        className="btn btn-small btn-primary"
                        disabled={starting}
                        onClick={async () => {
                          setStarting(true);
                          setAddProblem('');
                          /* All of them at once. In a row each write waits for
                             the one before it, which is the crawl this screen
                             was already told off for once. */
                          const { refused, why } = await moveIssues(
                            parked.map((i) => i.id),
                            'todo'
                          );
                          setStarting(false);
                          if (refused)
                            setAddProblem(
                              refused === parked.length
                                ? `None of them could be moved. ${why}`.trim()
                                : `${refused} of ${parked.length} could not be moved and are still listed. ${why}`.trim()
                            );
                        }}
                      >
                        {starting ? 'Moving…' : 'Move them to To Do'}
                      </button>
                    )}
                  </div>
                  <div className="grp-heading is-sub">Not on the board yet · {parked.length}</div>
                  <div className="grp-list is-contents">
                    {parked.map((i) => row(i, STATUS_LABELS[i.status], undefined, false, true))}
                  </div>
                  <div className="grp-heading is-sub">
                    On the board · {mine.length - parked.length}
                  </div>
                </>
              );
            })()}
            <div className="grp-list is-contents">
              {mine.length === 0 && (
                <p className="hint">
                  {mayFill
                    ? 'Nothing in it yet — add something from the backlog below.'
                    : 'Nothing in it.'}
                </p>
              )}
              {/*
                * EVERY CARD IN THE SPRINT APPEARS EXACTLY ONCE, in three groups
                * and in this order. `readyFirst` is the right ranking and the
                * wrong list on its own: it drops finished work AND blocked work,
                * so listing it beside the finished ones left a blocked card
                * showing nowhere at all — invisible on the one screen that is
                * supposed to say what this fortnight contains. Split from the
                * whole set rather than filtered twice, so the three groups
                * cannot overlap or leave a gap.
                */}
              {(() => {
                /*
                 * THE PARKED ONES ARE LISTED ABOVE AND MUST NOT BE LISTED
                 * AGAIN. Everything below groups `rest`, not `mine`, so the
                 * invariant this block was written around still holds: every
                 * card in the sprint appears exactly once. When the sprint is
                 * not running, or nothing is parked, `rest` IS `mine` and none
                 * of this changes.
                 */
                const parked = isRunning ? mine.filter((i) => i.status === 'backlog') : [];
                const isParked = new Set(parked.map((i) => i.id));
                const rest = parked.length ? mine.filter((i) => !isParked.has(i.id)) : mine;
                const finished = rest.filter((i) => i.status === 'done' || i.status === 'wontdo');
                // Best first, by the same rule the rest of the board uses — but
                // asked of this sprint's work rather than of everything, which
                // is the whole difference between flow and a timebox.
                // Ranked within this sprint, but blocked-ness is resolved against
                // the whole board (`issues`) — a sprint card whose blocker finished
                // outside the sprint is freed, not left stuck here (L164).
                const ready = readyFirst(rest, issues);
                const accounted = new Set([...finished, ...ready].map((i) => i.id));
                const stuck = rest.filter((i) => !accounted.has(i.id));
                /*
                 * ONE ROW SHAPE, AND EVERY PART OF IT HAS A PLACE.
                 *
                 * Vinnie, 15 August: "the descriptions next to 129 and 127 look
                 * out of place and look like a last-minute thought, the
                 * placement needs to be intentional." He was describing a real
                 * structural fault, not a preference.
                 *
                 * The row used to be a flat flex line — ref, title, then ONE
                 * span pushed right with `margin-left: auto` carrying either a
                 * one-word status or a whole sentence explaining a blocker.
                 * Those two things are not the same kind of thing and cannot
                 * share a slot: a sentence in a right-aligned column fights the
                 * title for width, so the two columns moved on every row
                 * depending on what was in them. Nothing lined up with anything,
                 * which is exactly what "jumbled" describes.
                 *
                 * So they are separated. The STATE is a short word in a column
                 * of its own, and every row's state sits under the last one. The
                 * REASON belongs to the card, so it goes underneath the card's
                 * title, in the title's column, quieter than it — where a reader
                 * meets it after the thing it is about rather than beside it.
                 */
                return (
                  <>
                    {/*
                      * `STATUS_LABELS`, NOT THE RAW VALUE. It printed
                      * "inprogress" — Vinnie: "the 'inprogress' looks like one
                      * word." It is one word, because it is the database's
                      * spelling of it, and the map that turns it into "In
                      * Progress" for every other screen was simply not used
                      * here.
                      */}
                    {ready.map((i) => row(i, STATUS_LABELS[i.status]))}
                    {stuck.map((i) => row(i, 'Waiting', blockerOf(i) || undefined))}
                    {/* Finished work stays listed: a sprint that shows only its
                        unfinished half looks emptier the better it is going. */}
                    {finished.map((i) =>
                      row(i, i.status === 'done' ? 'Done' : 'Not doing', undefined, true)
                    )}
                  </>
                );
              })()}
            </div>

            {/* WHY IT IS HERE AND NOT BESIDE THE BUTTON THAT CAUSED IT. Both
                lists can refuse a write — one puts cards in, the other takes
                them out — and a message that only appears under the lower list
                is a message somebody taking a card OUT scrolls past. Between
                the two, it is under whichever they just pressed. */}
            {addProblem && <p className="field-error">{addProblem}</p>}

            {/* ── Adding work to THIS sprint ──────────────────────────────── */}
            {mayFill && (
              <>
                <div className="grp-heading">
                  Add from the backlog
                  {/* "In no sprint" was true of finished work too, so the number
                      counted four times what the list could offer. It now says
                      what it is: unfinished work waiting to be planned. */}
                  <span className="grp-sub">
                    {backlog.length} unfinished {backlog.length === 1 ? 'card is' : 'cards are'} in
                    no sprint
                  </span>
                </div>
                {/* Offered once the backlog is long enough that finding
                    something is work. Below that it is a box with nothing to
                    do — the same rule the Teams screen uses. */}
                {backlog.length > 8 && (
                  <input
                    className="input grp-find"
                    value={find}
                    onChange={(e) => setFind(e.target.value)}
                    placeholder="Find a card by title or reference"
                  />
                )}
                {/*
                  * ── ADDING SEVERAL AT ONCE ──────────────────────────────
                  *
                  * Only once something is ticked. A bar that is always there,
                  * usually saying "0 chosen", is a bar the eye stops reading —
                  * and this one carries the only refusal message on the screen.
                  */}
                {picked.size > 0 && (
                  <div className="grp-bulk">
                    <span>
                      <b>{picked.size}</b> chosen
                    </span>
                    <button
                      className="btn btn-small btn-primary"
                      disabled={adding}
                      onClick={async () => {
                        setAdding(true);
                        setAddProblem('');
                        const ids = [...picked];
                        /*
                         * ALL OF THEM AT ONCE, not one after another. In a row
                         * each write waits for the one before it, so twenty
                         * cards is twenty round trips and the screen crawls —
                         * which is the complaint this answers. Each card has
                         * already moved on screen before any of them is sent.
                         */
                        const results = await Promise.allSettled(
                          ids.map((id) => putInSprint(id, s.id))
                        );
                        const refused = results.filter((r) => r.status === 'rejected');
                        setPicked(new Set());
                        setAdding(false);
                        /*
                         * WHAT DID NOT GO IN, COUNTED. "Some failed" leaves
                         * somebody comparing two lists by eye; the cards that
                         * were refused have already returned to this list, so
                         * the number and the list agree.
                         */
                        if (refused.length) {
                          const why = (refused[0] as PromiseRejectedResult).reason as Error;
                          setAddProblem(
                            refused.length === ids.length
                              ? `None of them could be added. ${why?.message ?? ''}`.trim()
                              : `${refused.length} of ${ids.length} could not be added and are still listed. ${why?.message ?? ''}`.trim()
                          );
                        }
                      }}
                    >
                      {adding ? 'Adding…' : `Add ${picked.size} to ${s.name}`}
                    </button>
                    <button className="link" disabled={adding} onClick={() => setPicked(new Set())}>
                      Clear
                    </button>
                  </div>
                )}
                <div className="grp-list">
                  {(() => {
                    const { shown, matching } = offerable();
                    {/* Two different empties. "Already in a sprint" said to a
                        board whose remaining work is simply all finished is a
                        sentence somebody would go looking for the cards of. */}
                    if (backlog.length === 0)
                      return (
                        <p className="hint">
                          {notInAnySprint(issues).length > 0
                            ? 'Everything left over is finished — there is nothing waiting to be planned.'
                            : 'Everything is already in a sprint.'}
                        </p>
                      );
                    if (matching === 0)
                      return <p className="hint">Nothing in the backlog matches “{find}”.</p>;
                    return (
                      <>
                        {/*
                          * THE SAME ROW SHAPE AS THE LIST ABOVE, because it is
                          * the same kind of list and the eye should not have to
                          * learn it twice. It also carried `i.priority` raw —
                          * "critical", lower-case, the database's spelling —
                          * beside a list that now says "In Progress" properly.
                          *
                          * THE SIZE IS SHOWN HERE AND NOWHERE ELSE ON THIS
                          * SCREEN, and this is the moment it matters: the line
                          * above says whether the fortnight is full, and this is
                          * where somebody decides what to put in it. Choosing
                          * blind and then reading the total is the wrong way
                          * round.
                          */}
                        {/* Openable for the same reason as the list above, and
                            the same way — Add stops the click travelling. */}
                        {shown.map((i) => (
                          <div
                            key={i.id}
                            className={`grp-row is-work is-openable is-pickable${picked.has(i.id) ? ' is-picked' : ''}`}
                            role="button"
                            tabIndex={0}
                            onClick={() => openIssue(i.id)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                openIssue(i.id);
                              }
                            }}
                          >
                            {/* Stops the click travelling, like Add does — a
                                tick that also opened the card would make
                                choosing several impossible. */}
                            <input
                              type="checkbox"
                              className="grp-pick"
                              checked={picked.has(i.id)}
                              aria-label={`Choose ${i.ref} to add`}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                setAddProblem('');
                                pick(i.id, e.target.checked);
                              }}
                            />
                            <span className="link sprint-ref">{i.ref}</span>
                            <div className="work-main">
                              <span className="grp-name">{i.title}</span>
                            </div>
                            <div className="work-meta">
                              <span className="work-state">
                                {PRIORITY_LABELS[i.priority]}
                                {i.size ? ` · ${SIZE_LABEL[i.size]}` : ''}
                              </span>
                              <button
                                className="btn btn-small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setAddProblem('');
                                  /*
                                   * A REFUSAL IS NOT NOTHING. This was `void`,
                                   * so a write the rules turned down threw into
                                   * an empty room and the row simply stayed
                                   * where it was — which is all anybody saw.
                                   */
                                  void putInSprint(i.id, s.id).catch((err: Error) =>
                                    setAddProblem(err?.message || 'That could not be added.')
                                  );
                                }}
                              >
                                Add
                              </button>
                            </div>
                          </div>
                        ))}
                        {matching > shown.length && (
                          <p className="hint">
                            Showing {shown.length} of {matching}, best first. Search above to narrow
                            it, or use the Backlog screen.
                          </p>
                        )}
                      </>
                    );
                  })()}
                </div>
              </>
            )}

            {/*
              * ── APPROVAL, WHERE THE THING IS — #332 ──────────────────────
              *
              * Vinnie, 1 September 2026: *"The sign-off feature needs to be
              * refined as it is in the documents section, which kid of doesnt
              * make sense."* It is asked for and answered here, on the
              * fortnight it is about, rather than on a screen somebody has to
              * remember to visit. Documents keeps its own; it stops being
              * where sign-offs live.
              *
              * ⚠️ FINISHED SPRINTS ONLY — ON A RANK BOARD. Approving completed
              * work that is still moving is not evidence. A slots board splits
              * the two: authorisation signs the PLAN (offered from planning) and
              * deliverables signs the finished work (offered at the close), so
              * its panel shows whatever the sprint's state — SprintSignoff draws
              * the right phase for it.
              */}
            {(slotsSignoff || s.state === 'closed') && <SprintSignoff sprint={s} />}
          </div>
        )}
      </div>
    );
  };

  const justClosed = sprints.find((s) => s.id === shipped);
  const beingReleased = sprints.find((s) => s.id === releasing);
  const beingFinished = sprints.find((s) => s.id === finishing);
  const asked = beingFinished && closingAsks(beingFinished, sprints, issues);

  /* `sprints-page` only caps the width. This screen is the one that had no cap
     at all, so it ran to whatever the window was while every other screen
     stopped somewhere different — the widest case of the drift Vinnie spotted. */
  return (
    <section className="panel sprints-page">
      {beingFinished && asked && (
        <FinishSprint
          sprint={beingFinished}
          left={asked.left}
          into={asked.into}
          busy={busy}
          problem={problem}
          choose={(what, alsoStart) => void finish(what, alsoStart)}
          cancel={() => { setFinishing(''); setProblem(''); }}
        />
      )}
      <h2>Sprints</h2>
      <p className="hint">
        A sprint is a stretch of time with a goal and a set of work in it. One runs at a time; the
        board and Next up follow whichever it is.
      </p>

      {/*
        * OPTION A — the offer, once the sprint has actually closed.
        *
        * ONE PRESS TO TAKE IT AND ONE TO DISMISS IT. A team that ships monthly
        * closes four sprints between releases and will see this four times;
        * anything heavier than a line with two buttons becomes something they
        * resent. Hidden entirely when the tool numbers this board's versions —
        * there is nothing to offer, because the number is not theirs to type.
        */}
      {justClosed && !releasing && mayRelease && !assistantVersions && (
        <div className="grid-warning cut-offer" role="status">
          <span>
            {justClosed.name} is finished. Record what it shipped?
          </span>
          <span className="cut-offer-acts">
            <button className="btn btn-small btn-primary" onClick={() => setReleasing(justClosed.id)}>
              Record a release
            </button>
            <button className="btn btn-small" onClick={() => setShipped('')}>
              Not now
            </button>
          </span>
        </div>
      )}

      {beingReleased && (
        <CutRelease
          sprintId={beingReleased.id}
          sprintName={beingReleased.name}
          onDone={() => {
            setReleasing('');
            setShipped('');
          }}
          onCancel={() => setReleasing('')}
        />
      )}

      {problem && (
        <div className="login-error" role="alert">
          {problem}
        </div>
      )}

      {sprints.length === 0 && (
        <p className="hint">
          No sprints yet. Plan the first one below — a fortnight is the usual length, and nothing
          stops you changing it.
        </p>
      )}

      <div className="people">{ordered.map(sprintCard)}</div>

      {/* ── Planning the next one ──────────────────────────────────────────── */}
      {mayPlan && !planning && (
        <div className="grp-add">
          <button className="btn btn-primary" onClick={openPlanner}>
            Plan a sprint
          </button>
        </div>
      )}

      {mayPlan && planning && (
        <div className="person-manage sprint-plan">
          <label className="stack">
            <span className="stack-lbl">Name <span className="req">*</span></span>
            <input
              className="input"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
          </label>
          <label className="stack">
            What is it for?{' '}
            <span className="hint">A sentence, not a list of tickets.</span>
            <input
              className="input"
              value={draft.goal}
              placeholder="e.g. Get sign-in working end to end"
              onChange={(e) => setDraft({ ...draft, goal: e.target.value })}
            />
          </label>
          <div className="two-up">
            <div className="stack">
              <label className="stack-lbl" htmlFor="plan-starts">Starts <span className="req">*</span></label>
              <DateField
                id="plan-starts"
                ariaLabel="Starts"
                variant="form"
                value={draft.startsOn}
                onChange={(v) =>
                  /*
                   * MOVING THE START MOVES THE END WITH IT, keeping the length.
                   * Otherwise changing the start of a suggested fortnight
                   * silently makes it a three-week sprint, and nobody reads the
                   * second date they did not touch.
                   */
                  setDraft({
                    ...draft,
                    startsOn: v,
                    endsOn: v ? addDays(v, DEFAULT_SPRINT_DAYS - 1) : draft.endsOn,
                  })
                }
                onInvalidChange={badPlan.mark('starts')}
              />
            </div>
            <div className="stack">
              <label className="stack-lbl" htmlFor="plan-ends">Ends <span className="req">*</span></label>
              <DateField
                id="plan-ends"
                ariaLabel="Ends"
                variant="form"
                value={draft.endsOn}
                onChange={(v) => setDraft({ ...draft, endsOn: v })}
                onInvalidChange={badPlan.mark('ends')}
              />
            </div>
          </div>
          <div className="grp-foot">
            <button className="btn btn-primary" onClick={() => void plan()} disabled={badPlan.any}>
              Plan it
            </button>
            <button className="btn" onClick={() => { setPlanning(false); setProblem(''); }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * THE DOCUMENTS FOR ONE SPRINT — #193.
 *
 * Shown only when the sprint has one, because most will not: a project that
 * does not work this way must not be told it is missing something. A card
 * carries nothing like this on purpose — Vinnie, 21 August: approval is given
 * for a grouped set of work, and asking for it card by card would be waste.
 *
 * ⚠️ NOT "SIGNED OFF FOR THIS SPRINT" — the one-ledger flow means every finished
 * slots sprint has a DRAFT deliverables document long before anybody signs it,
 * so a label claiming they are signed off is a lie the moment the document is
 * made. Each row carries its own state chip; the heading only says what these
 * are, not that they are done.
 */
function SprintDocuments({ sprintId }: { sprintId: string }) {
  const documents = useApp((s) => s.documents).filter((d) => d.sprint === sprintId);
  const drillTo = useApp((s) => s.drillTo);
  if (documents.length === 0) return null;
  return (
    <div className="sprint-docs">
      <span className="sprint-docs-lbl">Documents for this sprint</span>
      {documents.map((d) => (
        /*
         * OPENS THE DOCUMENT IT NAMES — Vinnie, 11 Sep: a status here saying a
         * document is in draft or awaiting sign-off is a dead end unless clicking
         * it takes you to the document. drillTo carries the way back (Sprints),
         * and Documents reveals `doc-<id>` — the same drill the "Open the change
         * request" link already uses, so a document and its status agree on where
         * it is answered.
         */
        <button
          key={d.id}
          type="button"
          className="sprint-doc"
          onClick={() => drillTo('documents', d.id)}
          title={`Open “${d.title}” on the Documents screen`}
        >
          <span className="doc-kind">{DOCUMENT_KIND[d.kind]}</span>
          <span className="sprint-doc-name">{d.title}</span>
          <span className={`doc-state is-${d.state}`}>{DOCUMENT_STATE[d.state]}</span>
        </button>
      ))}
    </div>
  );
}
